import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// useHeroBrief — Phase 2 hero-brief lifecycle hook.
//
// Wires a HeroBriefCard to the ai_briefs cache + tab-scoped edge
// function. Behavior:
//
//   1. On mount (and when restaurant_id resolves), look up the row in
//      ai_briefs for (restaurant_id, tab_key, sub_key) where
//      generated_at > now() - interval '6 hours'. Cache hit →
//      phase='ready'. Cache miss → phase='generating', fire the edge
//      function, the function writes the cache row and returns the
//      payload. Hook surfaces _empty payloads as phase='empty'.
//   2. regenerate() — explicit refresh, bypasses the 6h cache window.
//   3. invalidate() — bumps generated_at to epoch for the tuple so
//      the next mount/regenerate is forced to call the edge fn.
//   4. Caller passes opts.snapshot — already-built compact data
//      object the edge fn can use as the lazy-call payload.
//
// Tiny-data guard runs SERVER-SIDE in the edge function. The frontend
// hook never decides whether to short-circuit — that's not safe with
// a manipulable client snapshot.
//
// FIX H2: sub_key is normalized to '' (not null) to match the bare-
// column UNIQUE in ai_briefs. The edge fn writes '' explicitly.
//
// FIX H7: regenerate() and the initial cache-miss fetch share an
// inFlightRef synchronous guard — the phase-state-based check has a
// render-cycle race window that double-firing requests can slip
// through, draining Anthropic spend.
//
// FIX M3: the hook surfaces brief.retryable so the card can decide
// whether to show a Refresh button on empty/timeout payloads.

const TAB_FN_MAP = {
  huddle:                  'generate-huddle-brief',
  insights:                'generate-weekly-insights',
  stock:                   'generate-stock-hub-brief',
  schedule:                'generate-schedule-hub-brief',
  marketing:               'generate-marketing-brief',
  'stock/receive':         'generate-receive-brief',
  'stock/reorder':         'generate-reorder-brief',
  'stock/waste':           'generate-waste-brief',
  'schedule/catering':     'generate-catering-brief',
}

const CACHE_WINDOW_HOURS = 6

function fnNameFor(tabKey, subKey) {
  if (subKey) {
    const k = `${tabKey}/${subKey}`
    if (TAB_FN_MAP[k]) return TAB_FN_MAP[k]
  }
  return TAB_FN_MAP[tabKey] || null
}

// Normalize subKey to '' (parent-hub sentinel) so the cache lookup
// matches what the edge fn writes — bare-column unique constraint
// means `null` and `''` are different rows. (FIX H2)
function normalizeSubKey(subKey) {
  return subKey ? String(subKey) : ''
}

export function useHeroBrief(tabKey, subKey, opts) {
  const snapshot = opts?.snapshot ?? null
  const enabled  = opts?.enabled !== false  // default true

  const [phase, setPhase]       = useState('idle')   // idle | generating | ready | empty | error
  const [brief, setBrief]       = useState(null)
  const [error, setError]       = useState(null)
  const [generatedAt, setGenAt] = useState(null)
  const [restaurantId, setRid]  = useState(null)

  // Multi-unit scope. null for single-unit → resolve restaurant_id via
  // the legacy first-active lookup and send no restaurant_id in the
  // edge-fn body (byte-identical). Non-null for a grouped user → use
  // the selected unit directly as the cache scope AND pass it in the
  // request body so the verified-param path in _shared/auth.ts re-targets.
  const { currentRestaurantId } = useCurrentUnit()

  // Snapshot ref so regenerate() can pick up the latest snapshot
  // without forcing a re-bind. Snapshots in the parent are usually
  // memoized — the ref keeps regenerate() stable.
  const snapshotRef = useRef(snapshot)
  useEffect(() => { snapshotRef.current = snapshot }, [snapshot])

  // FIX H7: synchronous in-flight guard. Set true at the start of any
  // network call (initial cache-miss fetch or explicit regenerate),
  // cleared in finally. Prevents double-fires that the React phase
  // state can't catch (state updates aren't synchronous, so two
  // clicks of Refresh in quick succession would both see phase
  // !== 'generating' and proceed).
  const inFlightRef = useRef(false)

  // Resolve the restaurant_id. Grouped: use the selected unit directly
  // (skip the lookup) so the cache scope and edge-fn target follow the
  // UnitSwitcher and re-resolve on a switch. Single-unit (null): look up
  // the user's first active restaurant — unchanged legacy behavior.
  useEffect(() => {
    if (currentRestaurantId) {
      setRid(currentRestaurantId)
      return
    }
    let mounted = true
    supabase
      .from('restaurants')
      .select('id')
      .eq('active', true)
      .order('created_at')
      .limit(1)
      .single()
      .then(({ data, error: e }) => {
        if (!mounted) return
        if (e) {
          // eslint-disable-next-line no-console
          console.warn('[useHeroBrief] restaurant lookup failed', e)
          return
        }
        if (data?.id) setRid(data.id)
      })
    return () => { mounted = false }
  }, [currentRestaurantId])

  // Read from cache when (restaurant_id, tab_key, sub_key) tuple is
  // resolved. If cache hit within window, surface immediately.
  // Otherwise fire the edge fn.
  const lookupAndMaybeGenerate = useCallback(async ({ bypassCache = false } = {}) => {
    if (!enabled) return
    if (!restaurantId) return
    // FIX H7: short-circuit if a fetch is already in flight.
    if (inFlightRef.current) return
    const fnName = fnNameFor(tabKey, subKey)
    if (!fnName) {
      // Unknown tab — surface error rather than silently no-op.
      setPhase('error')
      setError('No brief defined for this tab.')
      return
    }

    setError(null)
    const normalizedSub = normalizeSubKey(subKey)

    // 1. Cache lookup (skipped on regenerate).
    if (!bypassCache) {
      const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
      // Bare-column lookup — sub_key is NOT NULL with default '' since
      // FIX H2, so we always send a string.
      const { data: rows, error: cacheErr } = await supabase
        .from('ai_briefs')
        .select('generated_at, payload')
        .eq('restaurant_id', restaurantId)
        .eq('tab_key', tabKey)
        .eq('sub_key', normalizedSub)
        .gte('generated_at', cutoff)
        .order('generated_at', { ascending: false })
        .limit(1)

      if (!cacheErr && rows && rows.length > 0) {
        const row = rows[0]
        setBrief(row.payload || null)
        setGenAt(row.generated_at)
        // _empty payloads — surface as phase='empty' so the card can
        // show a deterministic friendly nudge instead of a broken
        // "ready" with no headline.
        if (row.payload && row.payload._empty) {
          setPhase('empty')
        } else {
          setPhase('ready')
        }
        return
      }
      // cacheErr is non-fatal — we'll fall through and call the edge fn.
      if (cacheErr) {
        // eslint-disable-next-line no-console
        console.warn('[useHeroBrief] cache read failed', cacheErr)
      }
    }

    // 2. Cache miss → call the edge fn. FIX H7: claim the in-flight
    //    slot synchronously BEFORE the await so a second caller in
    //    the same tick sees inFlightRef.current === true.
    inFlightRef.current = true
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/${fnName}`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        // Single-unit: send only the snapshot — the edge fn resolves
        // restaurant_id from the JWT via auth.uid() (FIX H1). Grouped:
        // ALSO send the selected restaurant_id; _shared/auth.ts verifies
        // it against the caller's memberships before re-targeting (it is
        // never trusted blindly).
        body: JSON.stringify({
          snapshot: snapshotRef.current ?? null,
          ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
        }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        // Edge fn returned a structured error. Show calm fallback.
        setPhase('error')
        setError(data.message || "Couldn't pull today's brief — tap to try again.")
        return
      }
      setBrief(data || null)
      setGenAt(new Date().toISOString())
      if (data?._empty) {
        setPhase('empty')
      } else {
        setPhase('ready')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useHeroBrief] edge fn call failed', e)
      // Deterministic calm fallback. Never the raw error.
      setPhase('error')
      setError("Couldn't pull today's brief — tap to try again.")
    } finally {
      // FIX H7: always clear the in-flight slot, including on success.
      inFlightRef.current = false
    }
  }, [enabled, restaurantId, tabKey, subKey, currentRestaurantId])

  // Initial load — kick off cache lookup once restaurant_id resolves.
  useEffect(() => {
    if (!enabled) return
    if (!restaurantId) return
    lookupAndMaybeGenerate({ bypassCache: false })
    // We intentionally key only on the resolved tuple — re-running on
    // every snapshot change would defeat the cache.
  }, [enabled, restaurantId, tabKey, subKey, lookupAndMaybeGenerate])

  // Public regenerate — forces a fresh edge fn call. FIX H7: rely on
  // the synchronous inFlightRef rather than the async phase state.
  const regenerate = useCallback(() => {
    if (inFlightRef.current) return
    return lookupAndMaybeGenerate({ bypassCache: true })
  }, [lookupAndMaybeGenerate])

  // Public invalidate — bumps the cache row's generated_at to epoch
  // so the next read forces a regenerate. Used by write-action
  // handlers (e.g. logging waste should invalidate the waste brief).
  const invalidate = useCallback(async () => {
    if (!restaurantId) return
    const epoch = new Date(0).toISOString()
    const normalizedSub = normalizeSubKey(subKey)
    const { error: e } = await supabase
      .from('ai_briefs')
      .update({ generated_at: epoch })
      .eq('restaurant_id', restaurantId)
      .eq('tab_key', tabKey)
      .eq('sub_key', normalizedSub)
    if (e) {
      // eslint-disable-next-line no-console
      console.warn('[useHeroBrief] invalidate failed', e)
    }
  }, [restaurantId, tabKey, subKey])

  return useMemo(
    () => ({
      phase, brief, error, generatedAt,
      regenerate, invalidate,
    }),
    [phase, brief, error, generatedAt, regenerate, invalidate]
  )
}
